'use client'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'

// React-hook-form + zod imports
import { useForm } from 'react-hook-form'
import { signInSchema, TSignInSchema } from '@/schemas/auth'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../ui/card'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { useState, useCallback, useEffect } from 'react'
import { LockIcon, ShieldCheckIcon } from 'lucide-react'
import { SyncLoader } from 'react-spinners'
import { signIn, request2FACode, verify2FAAndSignIn } from '@/server/auth/actions'
import { purgeClientCaches } from '@/lib/auth-purge'
import { useRouter, Link } from '@/i18n/routing'
import { toast } from 'sonner'
import { setCurrentUser } from '@/store/currentUserSlice'
import { useAppDispatch } from '@/store'
import { useTranslations } from 'next-intl'
import { FireTransition } from './fire-transition'

const RESEND_COOLDOWN_SECONDS = 60

const cardStyle = {
  '--card': '0 0% 100%',
  '--card-foreground': '240 10% 3.9%',
  '--muted-foreground': '240 3.8% 46.1%',
  '--input': '240 5.9% 90%',
  '--border': '240 5.9% 90%',
  '--ring': '240 5.9% 10%',
  '--primary': '0 84% 60%',
  '--primary-foreground': '0 0% 100%'
} as React.CSSProperties

const SignInForm = () => {
  const router = useRouter()
  const t = useTranslations('SignIn')
  const dispatch = useAppDispatch()

  const [isPending, setIsPending] = useState(false)
  const [showFire, setShowFire] = useState(false)
  // 2FA step: set when the backend withholds the JWT for a 2FA-enabled account
  const [twoFAEmail, setTwoFAEmail] = useState<string | null>(null)
  const [otp, setOtp] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)

  const signInForm = useForm<TSignInSchema>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      username: '',
      password: '',
      rememberMe: false
    }
  })

  useEffect(() => {
    if (resendCooldown <= 0) return
    const id = setInterval(() => setResendCooldown((s) => s - 1), 1000)
    return () => clearInterval(id)
  }, [resendCooldown])

  // Shared post-login routine (password-only and 2FA paths converge here)
  const finishLogin = useCallback(async () => {
    await purgeClientCaches()
    const response = await fetch('/api/auth/current-user')

    if (!response.ok) {
      toast.error(t('error'))
      return
    }

    const data = await response.json()
    dispatch(setCurrentUser(data.data))

    toast.success(t('success'))
    window.dispatchEvent(new CustomEvent('auth-fire-start'))
    setShowFire(true)
  }, [dispatch, t])

  const onSubmit = async (values: TSignInSchema) => {
    setIsPending(true)

    try {
      const authenticated = await signIn(values)

      if ('requires2FA' in authenticated && authenticated.requires2FA) {
        setTwoFAEmail(authenticated.email)
        setOtp('')
        setResendCooldown(RESEND_COOLDOWN_SECONDS)
        toast.success(t('twoFASent'))
        return
      }

      if (authenticated.error) {
        toast.error(t('error'))
        return
      }

      if (authenticated.success) {
        await finishLogin()
      }
    } catch (error) {
      console.error(error)
      toast.error(t('error'))
    } finally {
      setIsPending(false)
    }
  }

  const onVerifyCode = async () => {
    if (!twoFAEmail || otp.length !== 6 || isPending) return
    setIsPending(true)

    try {
      const result = await verify2FAAndSignIn(twoFAEmail, otp)

      if (!result.ok) {
        toast.error(t('twoFAInvalid'))
        setOtp('')
        return
      }

      await finishLogin()
    } catch (error) {
      console.error(error)
      toast.error(t('error'))
    } finally {
      setIsPending(false)
    }
  }

  const onResendCode = async () => {
    if (!twoFAEmail || resendCooldown > 0 || isPending) return
    setIsPending(true)

    try {
      const sent = await request2FACode(twoFAEmail, signInForm.getValues('password'))
      if (!sent.ok) {
        toast.error(t('error'))
        return
      }
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
      toast.success(t('twoFASent'))
    } catch (error) {
      console.error(error)
      toast.error(t('error'))
    } finally {
      setIsPending(false)
    }
  }

  const backToSignIn = () => {
    setTwoFAEmail(null)
    setOtp('')
  }

  const handleFireComplete = useCallback(() => {
    router.replace('/dashboards')
  }, [router])

  // 2FA verification step
  if (twoFAEmail) {
    return (
      <>
        <FireTransition active={showFire} onComplete={handleFireComplete} />
        <Card className='w-96' style={cardStyle}>
          <CardHeader>
            <CardTitle>{t('twoFATitle')}</CardTitle>
            <CardDescription>
              {t('twoFADescription', { email: twoFAEmail })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='flex flex-col gap-4'>
              <Input
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onVerifyCode()
                }}
                placeholder='123456'
                inputMode='numeric'
                autoComplete='one-time-code'
                maxLength={6}
                disabled={isPending}
                autoFocus
                className='text-center text-2xl tracking-[0.5em] font-semibold'
              />
              <Button
                onClick={onVerifyCode}
                className='w-full flex gap-2 !bg-[#F04438] hover:!bg-[#D83A30] !text-white'
                disabled={isPending || otp.length !== 6}
              >
                {isPending ? (
                  <SyncLoader size={5} speedMultiplier={0.5} />
                ) : (
                  <>
                    <ShieldCheckIcon className='w-4 h-4' />
                    {t('twoFAVerify')}
                  </>
                )}
              </Button>
              <p className='text-sm text-muted-foreground text-center'>
                {resendCooldown > 0 ? (
                  t('twoFAResendIn', { seconds: resendCooldown })
                ) : (
                  <button
                    type='button'
                    onClick={onResendCode}
                    disabled={isPending}
                    className='font-medium text-[#F04438] hover:text-[#D83A30] hover:underline'
                  >
                    {t('twoFAResend')}
                  </button>
                )}
              </p>
              <button
                type='button'
                onClick={backToSignIn}
                disabled={isPending}
                className='text-sm text-muted-foreground hover:underline text-center'
              >
                {t('twoFABack')}
              </button>
            </div>
          </CardContent>
        </Card>
      </>
    )
  }

  return (
    <>
    <FireTransition active={showFire} onComplete={handleFireComplete} />
    <Card className='w-96' style={cardStyle}>
      <CardHeader>
        <CardTitle>{t('signIn')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...signInForm}>
          <form
            onSubmit={signInForm.handleSubmit(onSubmit)}
            className='flex flex-col gap-4'
          >
            <FormField
              control={signInForm.control}
              name='username'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('username')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('usernamePH')}
                      disabled={isPending}
                      type='text'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={signInForm.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('password')}</FormLabel>
                  <FormControl className=''>
                    <Input
                      placeholder='••••••'
                      disabled={isPending}
                      type='password'
                      {...field}
                      className='relative'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type='submit'
              className='w-full flex gap-2 !bg-[#F04438] hover:!bg-[#D83A30] !text-white'
              disabled={isPending}
            >
              {isPending ? (
                <SyncLoader size={5} speedMultiplier={0.5} />
              ) : (
                <>
                  <LockIcon className='w-4 h-4' />
                  {t('signIn')}
                </>
              )}
            </Button>
            <p className='text-sm text-muted-foreground text-center'>
              {t('forgotPasswordIntro')}{' '}
              <Link
                href='/forgot-password'
                className='font-medium text-[#F04438] hover:text-[#D83A30] hover:underline'
              >
                {t('forgotPassword')}
              </Link>
            </p>
          </form>
        </Form>
      </CardContent>
    </Card>
    </>
  )
}

export default SignInForm
